// src/pages/LandingPage.js
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../components/AuthContext";
import { ThemeContext } from "../context/ThemeContext";
import TutoringLeadPanel from "../components/TutoringLeadPanel";
import DemoMaturaNotice from "../components/DemoMaturaNotice";
import sections, { PROGRESS_ID_ALIASES } from "../sections";
import { lockedSubtopicsForStudents } from "../lessonAccess";
import {
  getMaturaCoursePreparationProgressId,
  getMaturaCourseProgressId,
  isMaturaCourseTopicVisible,
  MATURA_COURSE_STRUCTURE,
} from "../matura/maturaCourseStructure";
import {
  isMaturaCourseTopicPassed,
  MATURA_COURSE_CUSTOM_TARGET,
  readMaturaCourseCustomPlan,
  readMaturaCourseTarget,
} from "../matura/maturaCourseProgress";
import { OFFICIAL_MATURA_HISTORY } from "../matura/analysis/officialMaturaHistory";
import { buildMaturaCourseTargetCodes } from "../matura/analysis/maturaHistoryAnalysis";
import { buildMaturaAttemptRecommendations } from "../matura/analysis/maturaAttemptRecommendations";
import { hasMaturaCoursePreparation } from "../matura/preparation/maturaCoursePreparationRegistry";
import { normalizeMaturaAttemptRow } from "./matura/MaturaRecentAttemptsTable";
import { InvitesTutorPanel } from "./StudentDashboard/InvitesParents";
import { trackMarketingEvent } from "../utils/marketingMeasurement";
import { hasTutoringToolsAccess } from "../routes/tutoringToolsAccess";

const RIBBON_MAX_WIDTH = 1000;
const ACCENT = "#ffa726";
const LEARNING_PATH_STORAGE_KEY = "matematron.adminLearningPath.v1";
const FEEDBACK_HINT_VERSION = "v1";
const SHOW_TRIAL_UI = false;
const secondaryLearningLevels = [
  { id: "basic", label: "Podstawa" },
  { id: "extended", label: "Rozszerzenie" },
];

const learningPathStages = [
  {
    id: "primary",
    label: "Szkoła podstawowa",
    grades: ["4", "5", "6", "7", "8"],
    publishers: [
      { id: "gwo", label: "Matematyka z plusem (GWO)" },
      { id: "nowa-era", label: "Matematyka z kluczem (Nowa Era)" },
      { id: "wsip", label: "WSiP" },
    ],
  },
  {
    id: "secondary",
    label: "Liceum / technikum",
    grades: ["1", "2", "3", "4"],
    publishers: [
      { id: "pazdro", label: "Pazdro", level: "basic" },
      { id: "pazdro-extended", label: "Pazdro", level: "extended" },
      { id: "gwo", label: "Matematyka z plusem (GWO)", level: "basic" },
      { id: "gwo-extended", label: "Matematyka z plusem (GWO)", level: "extended" },
      { id: "nowa-era", label: "NOWA MATeMAtyka (Nowa Era)", level: "basic" },
      { id: "matematyka-nowa-era", label: "MATeMAtyka (Nowa Era)", level: "basic" },
      { id: "matematyka-nowa-era-extended", label: "MATeMAtyka (Nowa Era)", level: "extended" },
      { id: "prosto-do-matury", label: "Prosto do matury (Nowa Era)", level: "basic" },
      { id: "prosto-do-matury-extended", label: "Prosto do matury (Nowa Era)", level: "extended" },
    ],
  },
];

const indexedSubtopics = sections.flatMap((section, sectionIndex) => {
  let runningIndex = 0;
  const list = [];

  section.topics.forEach((topic) => {
    topic.subtopics.forEach((subtopic) => {
      list.push({
        id: subtopic.id,
        subtopicName: subtopic.name,
        topicName: topic.name,
        sectionTitle: section.title,
        sectionAdminOnly: !!section.adminOnly,
        sectionIndex,
        subtopicIndex: runningIndex,
        isQuiz: subtopic.type === "quiz",
      });
      runningIndex += 1;
    });
  });

  return list;
});

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCampaignDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function warsawDateKey(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Warsaw",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const value = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

function formatHms(ms) {
  if (!ms || ms <= 0) return "00:00:00";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function Accent({ children }) {
  return (
    <span style={{ color: "var(--color-accent)", fontWeight: 700 }}>
      {children}
    </span>
  );
}

const monoIconStyle = {
  width: 18,
  height: 18,
  display: "inline-block",
  verticalAlign: "middle",
};

function SectionsMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={monoIconStyle}>
      <path d="M4 6h16M4 12h16M4 18h10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="18" cy="18" r="2" fill="currentColor" />
    </svg>
  );
}

function ExamMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={monoIconStyle}>
      <path d="M7 3h8l4 4v14H7z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M15 3v4h4" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M10 12h6M10 16h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function SearchMonoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={monoIconStyle}>
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function MaturaLandingSummary({ user, authorizedFetch, isLightTheme, viewportWidth }) {
  const [summary, setSummary] = useState({
    completed: 0,
    mastered: 0,
    masteredOnly: 0,
    total: 0,
    allTotal: 0,
    lastPercent: null,
    averagePercent: null,
    continuePath: "/matura/sciezka",
    continueTitle: "",
    continueStage: "",
    continueOptional: false,
    targetLabel: "50%",
  });

  useEffect(() => {
    let cancelled = false;
    const courseStructure = MATURA_COURSE_STRUCTURE.map((section) => ({
      ...section,
      topics: section.topics.filter((topic) => (
        topic.taskTypes.length && isMaturaCourseTopicVisible(topic, user?.role)
      )),
    })).filter((section) => section.topics.length);
    const topics = courseStructure
      .flatMap((section) => section.topics)
      .filter((topic) => topic.taskTypes.length);
    const courseTarget = readMaturaCourseTarget(user?.email);
    const customPlan = readMaturaCourseCustomPlan(user?.email);
    const targetTopicCodes = courseTarget === MATURA_COURSE_CUSTOM_TARGET
      ? new Set(customPlan.codes.filter((code) => topics.some((topic) => topic.code === code)))
      : buildMaturaCourseTargetCodes(courseStructure, OFFICIAL_MATURA_HISTORY, courseTarget);
    const targetLabel = courseTarget === MATURA_COURSE_CUSTOM_TARGET
      ? customPlan.name || "własny"
      : `${courseTarget}%`;
    const targetTopics = topics.filter((topic) => targetTopicCodes.has(topic.code));
    const localProgress = (() => {
      try {
        const allProgress = JSON.parse(window.localStorage.getItem("userProgress") || "{}");
        return allProgress?.[user?.email] || {};
      } catch {
        return {};
      }
    })();
    const localCompleted = targetTopics.filter((topic) => (
      isMaturaCourseTopicPassed(localProgress[getMaturaCourseProgressId(topic.code)])
    )).length;
    const localNextTopic = targetTopics.find((topic) => (
      !isMaturaCourseTopicPassed(localProgress[getMaturaCourseProgressId(topic.code)])
    ));
    const localPreparationPassed = Boolean(localNextTopic && isMaturaCourseTopicPassed(
      localProgress[getMaturaCoursePreparationProgressId(localNextTopic.code)]
    ));
    setSummary((current) => ({
      ...current,
      total: targetTopics.length,
      allTotal: topics.length,
      completed: localCompleted,
      mastered: 0,
      masteredOnly: 0,
      continuePath: localNextTopic
        ? hasMaturaCoursePreparation(localNextTopic.code) && !localPreparationPassed
          ? `/matura/sciezka/przygotowanie/${localNextTopic.code}?mode=theory`
          : `/matura/sciezka/zadania/${localNextTopic.code}?mode=practice`
        : "/matura/sciezka",
      continueTitle: localNextTopic?.title || "",
      continueStage: localNextTopic
        ? hasMaturaCoursePreparation(localNextTopic.code) && !localPreparationPassed
          ? "Przygotowanie"
          : "Ćwiczenia maturalne"
        : "",
      continueOptional: false,
      targetLabel,
    }));

    if (!user?.email || !authorizedFetch) return undefined;

    (async () => {
      try {
        const [progressResponse, historyResponse] = await Promise.all([
          authorizedFetch("/api/progress/my"),
          authorizedFetch("/api/matura/results/history?examTaskId=matura_exam_A&limit=1000"),
        ]);
        const progressRows = progressResponse.ok ? await progressResponse.json() : [];
        const historyData = historyResponse.ok ? await historyResponse.json() : {};
        const progressById = {};
        (Array.isArray(progressRows) ? progressRows : []).forEach((row) => {
          if (!String(row?.taskId || "").startsWith("matura_course")) return;
          progressById[row.taskId] = {
            score: Number.isFinite(row.lastScore) ? row.lastScore : row.score,
            bestScore: Number.isFinite(row.bestScore) ? row.bestScore : row.score,
          };
        });
        const passedCodes = new Set(targetTopics
          .filter((topic) => isMaturaCourseTopicPassed(progressById[getMaturaCourseProgressId(topic.code)]))
          .map((topic) => topic.code));
        const recentRows = (Array.isArray(historyData?.history) ? historyData.history : [])
          .map(normalizeMaturaAttemptRow)
          .filter(Boolean)
          .filter((row) => row.snapshotId)
          .slice(0, 3);
        const snapshots = await Promise.all(recentRows.map(async (row) => {
          const response = await authorizedFetch(`/api/snapshot/${encodeURIComponent(row.snapshotId)}`);
          return response.ok ? response.json() : null;
        }));
        const examAnalysis = buildMaturaAttemptRecommendations({
          snapshots: snapshots.filter(Boolean),
          courseStructure,
          officialSheets: OFFICIAL_MATURA_HISTORY,
        });
        const masteredCodes = new Set((examAnalysis.masteredTopics || []).map((topic) => topic.code));
        const recommendedTopic = targetTopics.find((topic) => (
          !passedCodes.has(topic.code) && !masteredCodes.has(topic.code)
        ));
        const optionalTopic = recommendedTopic ? null : targetTopics.find((topic) => (
          !passedCodes.has(topic.code) && masteredCodes.has(topic.code)
        ));
        const nextTopic = recommendedTopic || optionalTopic;
        const preparationPassed = Boolean(nextTopic && isMaturaCourseTopicPassed(
          progressById[getMaturaCoursePreparationProgressId(nextTopic.code)]
        ));
        const attempts = (Array.isArray(historyData?.history) ? historyData.history : [])
          .map((row) => {
            const points = Number(row.points ?? row.pointsEarned);
            const maxPoints = Number(row.maxPoints ?? row.max ?? row.totalPoints);
            return maxPoints > 0 ? Math.round((points * 100) / maxPoints) : null;
          })
          .filter(Number.isFinite);
        if (!cancelled) {
          setSummary({
            completed: passedCodes.size,
            mastered: targetTopics.filter((topic) => masteredCodes.has(topic.code)).length,
            masteredOnly: targetTopics.filter((topic) => (
              masteredCodes.has(topic.code) && !passedCodes.has(topic.code)
            )).length,
            total: targetTopics.length,
            allTotal: topics.length,
            lastPercent: attempts.length ? attempts[0] : null,
            averagePercent: attempts.length
              ? Math.round(attempts.reduce((sum, value) => sum + value, 0) / attempts.length)
              : null,
            continuePath: nextTopic
              ? hasMaturaCoursePreparation(nextTopic.code) && !preparationPassed && !optionalTopic
                ? `/matura/sciezka/przygotowanie/${nextTopic.code}?mode=theory`
                : `/matura/sciezka/zadania/${nextTopic.code}?mode=practice`
              : "/matura/sciezka",
            continueTitle: nextTopic?.title || "",
            continueStage: nextTopic
              ? hasMaturaCoursePreparation(nextTopic.code) && !preparationPassed && !optionalTopic
                ? "Przygotowanie"
                : "Ćwiczenia maturalne"
              : "",
            continueOptional: Boolean(optionalTopic),
            targetLabel,
          });
        }
      } catch {
        // Lokalny postęp nadal daje użyteczne podsumowanie.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, user?.email, user?.role]);

  const statStyle = {
    minWidth: 0,
    padding: viewportWidth <= 680 ? "9px 10px" : "11px 13px",
    borderRadius: 8,
    border: isLightTheme
      ? "1px solid rgba(92, 57, 14, 0.18)"
      : "1px solid rgba(255, 255, 255, 0.16)",
    background: isLightTheme ? "rgba(255,255,255,0.48)" : "rgba(0,0,0,0.18)",
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 8,
      }}
    >
      <div style={statStyle}>
        <div style={{ fontSize: "0.74rem", opacity: 0.72 }}>Zaliczone zagadnienia</div>
        <strong>
          {summary.completed}/{summary.total}
          {summary.allTotal > summary.total ? ` (z ${summary.allTotal} wszystkich)` : ""}
        </strong>
      </div>
      <div style={statStyle}>
        <div style={{ fontSize: "0.74rem", opacity: 0.72 }}>Opanowane w arkuszach</div>
        <strong>{summary.mastered}/{summary.total}</strong>
      </div>
      <div style={statStyle}>
        <div style={{ fontSize: "0.74rem", opacity: 0.72 }}>Ostatni arkusz</div>
        <strong>{summary.lastPercent === null ? "Brak podejścia" : `${summary.lastPercent}%`}</strong>
      </div>
      <div style={statStyle}>
        <div style={{ fontSize: "0.74rem", opacity: 0.72 }}>Średni wynik</div>
        <strong>{summary.averagePercent === null ? "Brak danych" : `${summary.averagePercent}%`}</strong>
      </div>
      {summary.masteredOnly > 0 ? (
        <div
          style={{
            gridColumn: "1 / -1",
            padding: "8px 10px",
            borderLeft: "3px solid var(--color-accent)",
            fontSize: "0.76rem",
            lineHeight: 1.35,
            opacity: 0.82,
          }}
        >
          {summary.masteredOnly} {summary.masteredOnly === 1 ? "zagadnienie jest" : "zagadnienia są"} już
          opanowane w arkuszach. Ścieżka najpierw zaproponuje inne tematy; do tych możesz wrócić później,
          aby zaliczyć je także quizem.
        </div>
      ) : null}
      <div
        style={{
          gridColumn: "1 / -1",
          display: "grid",
          gridTemplateColumns: viewportWidth <= 520 ? "1fr" : "repeat(2, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        <Link
          to="/matura/sciezka"
          className="btn-primary"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 42,
            padding: "10px 14px",
            textDecoration: "none",
            fontWeight: 850,
            textAlign: "center",
          }}
        >
          Przejdź do ścieżki →
        </Link>
        {summary.continueTitle ? (
          <Link
            to={summary.continuePath}
            className="btn-secondary"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 42,
              padding: "9px 13px",
              textDecoration: "none",
              fontWeight: 800,
              textAlign: "center",
            }}
            title={`Zalecane zagadnienie: ${summary.continueTitle}`}
          >
            <span>
              Kontynuuj naukę (zakres {summary.targetLabel}) →
              <span style={{ display: "block", marginTop: 2, fontSize: "0.7rem", opacity: 0.72 }}>
                {summary.continueOptional ? "Opcjonalne domknięcie" : summary.continueStage}: {summary.continueTitle}
              </span>
            </span>
          </Link>
        ) : null}
      </div>
    </div>
  );
}

function StudentTutoringShortcut({ user, authorizedFetch, reloadAccess }) {
  const navigate = useNavigate();
  const [confirmedTutors, setConfirmedTutors] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);

  const refresh = useCallback(async () => {
    if (!user?.email || !authorizedFetch || user.role !== "student") return;
    const email = encodeURIComponent(user.email);
    try {
      const [confirmedResponse, pendingResponse] = await Promise.all([
        authorizedFetch(`/api/tutor/confirmed-for-student/${email}`),
        authorizedFetch(`/api/tutor/pending/${email}`),
      ]);
      const [confirmedData, pendingData] = await Promise.all([
        confirmedResponse.ok ? confirmedResponse.json() : {},
        pendingResponse.ok ? pendingResponse.json() : {},
      ]);
      setConfirmedTutors(Array.from(new Set(
        (confirmedData.tutors || []).map((value) => String(value).toLowerCase())
      )));
      setPendingInvites(Array.from(new Set(
        (pendingData.pending || []).map((value) => String(value).toLowerCase())
      )));
    } catch {
      // Brak danych o korepetycjach nie blokuje strony głównej.
    }
  }, [authorizedFetch, user?.email, user?.role]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const respondToInvite = useCallback(async (tutorEmail, action) => {
    if (!user?.email) return;
    try {
      const response = await authorizedFetch(`/api/tutor/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tutorEmail, studentEmail: user.email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok === false) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }
      await Promise.all([refresh(), reloadAccess?.()]);
    } catch (error) {
      window.alert(`Nie udało się odpowiedzieć na zaproszenie: ${error?.message || "spróbuj ponownie"}`);
    }
  }, [authorizedFetch, refresh, reloadAccess, user?.email]);

  if (user?.role !== "student" || (!confirmedTutors.length && !pendingInvites.length)) return null;

  return (
    <div style={{ width: "100%", maxWidth: 980, margin: "0 auto", display: "grid", gap: 10 }}>
      {confirmedTutors.length > 0 ? (
        <div
          className="glass-container"
          style={{ padding: 12, borderRadius: 12, border: "1px solid rgba(255,167,38,0.5)" }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ marginRight: "auto" }}>
              <div style={{ fontWeight: 800 }}>Korepetycje</div>
              <div style={{ marginTop: 2, fontSize: "0.82rem", opacity: 0.76 }}>
                Lekcje, prace domowe i kontakt z korepetytorem.
              </div>
            </div>
            <button type="button" className="btn-primary" onClick={() => navigate("/uczen/korepetycje")}>
              Otwórz korepetycje →
            </button>
          </div>
        </div>
      ) : null}
      {pendingInvites.length > 0 ? (
        <InvitesTutorPanel
          pendingInvites={pendingInvites}
          readOnly={false}
          onAccept={(email) => respondToInvite(email, "accept")}
          onReject={(email) => respondToInvite(email, "reject")}
        />
      ) : null}
    </div>
  );
}

// Prosty, widoczny baner informacyjny (1–2 linijki)
function loadLearningPathDraft() {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(LEARNING_PATH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

function saveLearningPathDraft(path) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LEARNING_PATH_STORAGE_KEY, JSON.stringify(path));
}

function AdminLearningPathPanel() {
  const navigate = useNavigate();
  const [learningPath, setLearningPath] = useState(() => {
    const saved = loadLearningPathDraft();
    return {
      stage: saved?.stage || "",
      grade: saved?.grade || "",
      publisher: saved?.publisher || "",
    };
  });

  const selectedStage = useMemo(
    () => learningPathStages.find((stage) => stage.id === learningPath.stage) || null,
    [learningPath.stage]
  );

  useEffect(() => {
    saveLearningPathDraft(learningPath);
  }, [learningPath]);

  const updateLearningPath = (patch) => {
    setLearningPath((current) => {
      const next = { ...current, ...patch };
      if (patch.stage && patch.stage !== current.stage) {
        next.grade = "";
        next.publisher = "";
      }
      return next;
    });
  };

  const selectedPublisher =
    selectedStage?.publishers.find((publisher) => publisher.id === learningPath.publisher)
      ?.label || "-";

  const buttonStyle = (active, disabled = false, large = false) => ({
    border: active ? "1px solid var(--color-accent)" : "1px solid rgba(255,255,255,0.24)",
    background: active ? "rgba(255,167,38,0.18)" : "rgba(255,255,255,0.045)",
    color: disabled ? "rgba(255,255,255,0.45)" : "var(--color-text)",
    borderRadius: 12,
    padding: large ? "13px 16px" : "9px 12px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "var(--font-main)",
    fontSize: large ? "1rem" : "0.92rem",
    lineHeight: 1.2,
    minHeight: large ? 52 : 40,
  });

  return (
    <div
      className="glass-container"
      style={{
        width: "100%",
        maxWidth: "760px",
        margin: "0 auto 12px",
        borderRadius: 14,
        padding: "14px",
        border: "1px solid rgba(255,167,38,0.35)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: "0.95rem", fontWeight: 800 }}>Ścieżka podręcznikowa</div>
          <div style={{ fontSize: "0.82rem", opacity: 0.78 }}>
            Wybór startowy. Po spisach treści podepniemy tu właściwe trasy.
          </div>
        </div>
        <div style={{ fontSize: "0.82rem", opacity: 0.74 }}>
          {selectedStage?.label || "wybierz etap"} / klasa {learningPath.grade || "-"} / {selectedPublisher}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 8 }}>
        {learningPathStages.map((stage) => (
          <button
            key={stage.id}
            type="button"
            style={buttonStyle(learningPath.stage === stage.id, false, true)}
            onClick={() => updateLearningPath({ stage: stage.id })}
          >
            {stage.label}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
        <div>
          <div style={{ fontSize: "0.78rem", opacity: 0.7, marginBottom: 6 }}>Klasa</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(selectedStage?.grades || []).map((grade) => (
              <button
                key={grade}
                type="button"
                style={buttonStyle(learningPath.grade === grade)}
                onClick={() => updateLearningPath({ grade })}
              >
                {grade}
              </button>
            ))}
            {!selectedStage && (
              <button type="button" style={buttonStyle(false, true)} disabled>
                Najpierw wybierz etap
              </button>
            )}
          </div>
        </div>

        <div>
          <div style={{ fontSize: "0.78rem", opacity: 0.7, marginBottom: 6 }}>Wydawnictwo</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(selectedStage?.publishers || []).map((publisher) => (
              <button
                key={publisher.id}
                type="button"
                style={buttonStyle(learningPath.publisher === publisher.id)}
                onClick={() => updateLearningPath({ publisher: publisher.id })}
              >
                {publisher.label}
              </button>
            ))}
            {!selectedStage && (
              <button type="button" style={buttonStyle(false, true)} disabled>
                Najpierw wybierz etap
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
        <button
          type="button"
          className="btn-primary"
          onClick={() => navigate("/admin/sciezka-spis")}
          style={{ borderRadius: 12, padding: "10px 14px" }}
        >
          Przejdź do spisu
        </button>
      </div>
    </div>
  );
}

function InfoBanner({ variant = "info", children }) {
  // variant: "promo" | "info"
  const isPromo = variant === "promo";

  return (
    <div
      className="glass-container"
      style={{
        width: "100%",
        borderRadius: 16,
        padding: "12px 14px",
        border: isPromo ? "1px solid rgba(255,167,38,0.95)" : "1px solid rgba(255,255,255,0.22)",
        boxShadow: "0 10px 24px rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: "0.98rem",
          lineHeight: 1.35,
          fontWeight: 650,
          opacity: 0.98,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// Uniwersalny kafelek – kolor/kontrast bierze z .glass-container
function LandingTile({
  to,
  title,
  icon,
  description,
  disabled = false,
  disabledNote,
  busy = false,
  ctaLabel,
  onClick,
  minHeight = 230,
  compact = false,
}) {
  const tileStyle = {
    borderRadius: "16px",
    padding: compact ? "14px 14px" : "18px 18px",
    border: disabled ? "1px solid rgba(255,255,255,0.25)" : "1px solid #ffa726",
    minHeight: `${minHeight}px`,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: disabled
      ? "0 4px 12px rgba(0,0,0,0.35)"
      : "0 8px 24px rgba(0,0,0,0.45)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease",
    opacity: disabled ? 0.7 : busy ? 0.92 : 1,
    cursor: disabled ? "not-allowed" : busy ? "progress" : "pointer",
  };

  const handleMouseEnter = (e) => {
    if (disabled || busy) return;
    e.currentTarget.style.transform = "translateY(-3px)";
    e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.6)";
  };

  const handleMouseLeave = (e) => {
    if (disabled || busy) return;
    e.currentTarget.style.transform = "none";
    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.45)";
  };

  const inner = (
    <div
      className="glass-container"
      style={tileStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      title={disabled ? disabledNote || "Funkcja będzie dostępna wkrótce" : undefined}
    >
      <div>
        <h2
          style={{
            fontSize: compact ? "1rem" : "1.15rem",
            marginBottom: compact ? "8px" : "10px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {icon ? <span style={{ display: "inline-flex" }}>{icon}</span> : null}
          {title}
        </h2>

        <p
          style={{
            fontSize: compact ? "0.88rem" : "0.95rem",
            lineHeight: 1.45,
            opacity: 0.9,
            marginBottom: compact ? "12px" : "18px",
          }}
        >
          {description}
        </p>
      </div>

      <span
        style={{
          fontSize: "0.9rem",
          fontWeight: 600,
          opacity: 0.95,
        }}
      >
        {disabled ? disabledNote || "Wkrótce dostępne" : ctaLabel || "Otwórz →"}
      </span>
    </div>
  );

  if (disabled) {
    return <div style={{ textDecoration: "none", color: "inherit" }}>{inner}</div>;
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        style={{
          textDecoration: "none",
          color: "inherit",
          display: "block",
          height: "100%",
          width: "100%",
          border: 0,
          padding: 0,
          margin: 0,
          background: "transparent",
          textAlign: "left",
          font: "inherit",
        }}
      >
        {inner}
      </button>
    );
  }
  return (
    <Link
      to={to}
      style={{
        textDecoration: "none",
        color: "inherit",
        display: "block",
        height: "100%",
      }}
    >
      {inner}
    </Link>
  );
}

export default function LandingPage() {
  const { user, entitlements, learningPathMode, authorizedFetch, reloadAccess } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [busyDemoMatura, setBusyDemoMatura] = useState(false);
  const [showBelowFold] = useState(true);
  const [nowTs, setNowTs] = useState(Date.now());
  const [searchQuery, setSearchQuery] = useState("");
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [learningProfileDismissed, setLearningProfileDismissed] = useState(false);
  const [feedbackHintDismissed, setFeedbackHintDismissed] = useState(true);
  const [myProgressSummary, setMyProgressSummary] = useState(null);
  const feedbackHintTrackedForRef = useRef("");
  const [publicCampaign, setPublicCampaign] = useState(null);
  const [myCampaign, setMyCampaign] = useState(null);
  const [campaignSaleDismissed, setCampaignSaleDismissed] = useState(true);
  const [learningProfileExpanded, setLearningProfileExpanded] = useState(false);
  const [showLearningProfileLaterInfo, setShowLearningProfileLaterInfo] = useState(false);
  const [studentLearningProfile, setStudentLearningProfile] = useState({
    stage: "",
    grade: "",
    level: "",
    publisher: "",
  });

  const handleOpenDemoMatura = () => {
    if (busyDemoMatura) return;
    setBusyDemoMatura(true);
    requestAnimationFrame(() => {
      navigate("/demo/matura/sciezka?mode=diagnosis#matura-exam-start");
    });
  };

  useEffect(() => {
    // 1x dziennie na urządzenie/przeglądarkę (żeby nie nabijać przy każdym odświeżeniu)
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const key = "mt_visit_day";
    const last = localStorage.getItem(key);
    if (last === today) return;
    localStorage.setItem(key, today);

    // ping do backendu – zapisze IP (z X-Forwarded-For), UA i path
    fetch("/api/traffic/visit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: window.location.pathname + window.location.search }),
      credentials: "include",
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    authorizedFetch("/api/payments/launch-campaign/public")
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (!cancelled) setPublicCampaign(data && typeof data === "object" ? data : null);
      })
      .catch(() => {
        if (!cancelled) setPublicCampaign(null);
      });
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  const loadMyCampaign = useCallback(async () => {
    if (!user || user.role !== "student") {
      setMyCampaign(null);
      return null;
    }
    try {
      const response = await authorizedFetch("/api/payments/launch-campaign/my");
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const normalized = data && typeof data === "object" ? data : null;
      setMyCampaign(normalized);
      return normalized;
    } catch {
      setMyCampaign(null);
      return null;
    }
  }, [authorizedFetch, user]);

  useEffect(() => {
    loadMyCampaign();
  }, [loadMyCampaign]);

  const campaignSaleUserKey = user
    ? String(user._id || user.id || user.email || "account")
    : "";
  const campaignSaleStorageKey = campaignSaleUserKey
    ? `matematron.campaignSaleDismissed.${campaignSaleUserKey}`
    : "";

  useEffect(() => {
    if (!campaignSaleStorageKey) {
      setCampaignSaleDismissed(false);
      return;
    }
    setCampaignSaleDismissed(
      localStorage.getItem(campaignSaleStorageKey) === warsawDateKey()
    );
  }, [campaignSaleStorageKey]);

  const dismissCampaignSale = () => {
    if (campaignSaleStorageKey) {
      localStorage.setItem(campaignSaleStorageKey, warsawDateKey());
    }
    setCampaignSaleDismissed(true);
    trackMarketingEvent("campaign_sale_banner_dismiss", { placement: "logged_home" });
  };

  useEffect(() => {
    const refreshAfterAccessChange = () => {
      loadMyCampaign();
    };
    window.addEventListener("matematron:access-updated", refreshAfterAccessChange);
    return () => window.removeEventListener("matematron:access-updated", refreshAfterAccessChange);
  }, [loadMyCampaign]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const feedbackHintUserKey = user
    ? String(user._id || user.id || user.email || "account")
    : "";
  const feedbackHintStorageKey = feedbackHintUserKey
    ? `matematron.feedbackHint.${FEEDBACK_HINT_VERSION}.${feedbackHintUserKey}`
    : "";

  useEffect(() => {
    if (!feedbackHintStorageKey) {
      setFeedbackHintDismissed(true);
      return;
    }
    setFeedbackHintDismissed(localStorage.getItem(feedbackHintStorageKey) === "dismissed");
  }, [feedbackHintStorageKey]);

  useEffect(() => {
    if (!user || feedbackHintDismissed || !feedbackHintStorageKey) return;
    if (feedbackHintTrackedForRef.current === feedbackHintStorageKey) return;
    feedbackHintTrackedForRef.current = feedbackHintStorageKey;
    trackMarketingEvent("feedback_hint_view", { placement: "landing_top" });
  }, [feedbackHintDismissed, feedbackHintStorageKey, user]);

  const dismissFeedbackHint = () => {
    if (feedbackHintStorageKey) localStorage.setItem(feedbackHintStorageKey, "dismissed");
    setFeedbackHintDismissed(true);
  };

  const openFeedbackFromHint = () => {
    trackMarketingEvent("feedback_hint_click", { placement: "landing_top" });
    window.dispatchEvent(new Event("matematron:open-feedback"));
  };

  useEffect(() => {
    let cancelled = false;
    if (!user || !["student", "admin"].includes(String(user.role || ""))) {
      setMyProgressSummary(null);
      return undefined;
    }

    const canonicalIds = new Set(
      sections.flatMap((section) =>
        (section.topics || []).flatMap((topic) =>
          (topic.subtopics || []).map((subtopic) => String(subtopic.id || ""))
        )
      )
    );

    authorizedFetch("/api/progress/my")
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((rows) => {
        if (cancelled) return;
        const canonicalRows = (Array.isArray(rows) ? rows : []).filter((row) => {
          const mappedId = PROGRESS_ID_ALIASES?.[row?.taskId] || row?.taskId;
          return canonicalIds.has(String(mappedId || ""));
        });
        setMyProgressSummary({ count: canonicalRows.length });
      })
      .catch(() => !cancelled && setMyProgressSummary(null));

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, user]);

  const dismissLearningProfilePrompt = () => {
    setLearningProfileDismissed(true);
  };

  const updateStudentLearningProfile = (patch) => {
    setStudentLearningProfile((current) => {
      const next = { ...current, ...patch };
      if (patch.stage && patch.stage !== current.stage) {
        next.grade = "";
        next.level = "";
        next.publisher = "";
      }
      if (patch.level && patch.level !== current.level) {
        next.publisher = "";
      }
      return next;
    });
  };

  // na start: zalogowany = ma dostęp
  const accessLevel = user ? "logged_with_access" : "guest";
  const isAdmin = user?.role === "admin";
  const canUseTutoringTools = hasTutoringToolsAccess(user, entitlements);
  const canSeeLearningPathExperiments =
    learningPathMode === "all" || (learningPathMode === "admin" && isAdmin);
  const isLightTheme = theme === "light";
  const trialValidToTs = entitlements?.trialValidTo
    ? new Date(entitlements.trialValidTo).getTime()
    : null;
  const trialLeftMs = trialValidToTs ? trialValidToTs - nowTs : 0;
  const trialActive = !!entitlements?.trialActive && trialLeftMs > 0;
  const hasPaidAccess = !!entitlements?.active && entitlements?.sku && entitlements?.sku !== "trial";
  const showTrialBanner = SHOW_TRIAL_UI && !!user && !hasPaidAccess && trialActive;
  const showAdminTrialPlaceholder =
    SHOW_TRIAL_UI && user?.role === "admin" && !showTrialBanner;
  const studentLearningStage =
    learningPathStages.find((stage) => stage.id === studentLearningProfile.stage) || null;
  const studentPublisherOptions =
    studentLearningStage?.id === "secondary" && studentLearningProfile.level
      ? studentLearningStage.publishers.filter((publisher) => publisher.level === studentLearningProfile.level)
      : studentLearningStage?.publishers || [];
  const selectedStudentLevel =
    secondaryLearningLevels.find((level) => level.id === studentLearningProfile.level) || null;
  const selectedStudentPublisher =
    studentPublisherOptions.find((publisher) => publisher.id === studentLearningProfile.publisher) || null;

  let headerBlock = null;
  let tilesBlock = null;
  let heroBlock = null;
  let searchBlock = null;

  const trimmedSearch = searchQuery.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (trimmedSearch.length < 3) return [];
    return indexedSubtopics.filter((item) => {
      if (item.sectionAdminOnly && !isAdmin) return false;
      const text = `${item.subtopicName} ${item.topicName} ${item.sectionTitle}`.toLowerCase();
      return text.includes(trimmedSearch);
    });
  }, [trimmedSearch, isAdmin]);

  const topBanner = user && !feedbackHintDismissed ? (
    <div
      className="glass-container"
      style={{
        width: "100%",
        borderRadius: 14,
        padding: viewportWidth <= 620 ? "12px" : "12px 14px",
        border: "1px solid rgba(255,153,0,0.55)",
        background: "rgba(255,153,0,0.07)",
        display: "flex",
        flexWrap: viewportWidth <= 620 ? "wrap" : "nowrap",
        alignItems: "center",
        gap: 12,
        lineHeight: 1.4,
      }}
    >
      <button
        type="button"
        onClick={openFeedbackFromHint}
        aria-label="Otwórz formularz zgłoszenia"
        title="Zgłoś problem lub podziel się opinią"
        style={{
          width: 42,
          height: 42,
          flex: "0 0 42px",
          borderRadius: 11,
          border: "1px solid rgba(255,255,255,0.85)",
          background: "transparent",
          color: "var(--color-text)",
          fontSize: 22,
          cursor: "pointer",
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: 23, height: 23 }}>
          <path d="M5 5h14v10H8l-3 3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          <path d="M12 8v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M12 15h.01" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </button>
      <div style={{ flex: "1 1 280px", minWidth: 0, fontSize: "0.94rem" }}>
        <strong>Coś jest niejasne albo nie działa?</strong>{" "}
        W nagłówku u góry znajdziesz ikonę zgłoszenia. Możesz przez nią szybko opisać problem,
        przesłać sugestię i dołączyć zrzut ekranu.
      </div>
      <button
        type="button"
        className="btn-secondary"
        onClick={openFeedbackFromHint}
        style={{ maxWidth: "100%", whiteSpace: "nowrap", padding: "8px 11px", borderRadius: 10 }}
      >
        Zgłoś problem
      </button>
      <button
        type="button"
        onClick={dismissFeedbackHint}
        aria-label="Zamknij informację o zgłoszeniach"
        title="Nie pokazuj ponownie"
        style={{
          alignSelf: "center",
          flex: "0 0 auto",
          marginLeft: "auto",
          border: 0,
          background: "transparent",
          color: "var(--color-text)",
          opacity: 0.72,
          cursor: "pointer",
          fontSize: 22,
          lineHeight: 1,
          padding: 4,
        }}
      >
        ×
      </button>
    </div>
  ) : null;

  const myProgressBanner = user && myProgressSummary?.count > 0 ? (
    <div
      className="glass-container"
      style={{
        width: "100%",
        borderRadius: 14,
        padding: viewportWidth <= 620 ? "12px" : "12px 14px",
        border: "1px solid rgba(255,153,0,0.55)",
        background: "rgba(255,153,0,0.07)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
      }}
    >
      <div style={{ minWidth: 0, lineHeight: 1.4 }}>
        <strong>Mój postęp</strong>
        <div style={{ marginTop: 3, fontSize: "0.9rem", opacity: 0.82 }}>
          {myProgressSummary.count === 1
            ? "Masz 1 rozpoczęte zagadnienie z zapisanym wynikiem."
            : `Masz ${myProgressSummary.count} rozpoczętych zagadnień z zapisanymi wynikami.`}
        </div>
      </div>
      <button type="button" className="btn-primary" onClick={() => navigate("/moj-postep")}>
        Zobacz postęp
      </button>
    </div>
  ) : null;
  const yearEndCampaignOpen =
    publicCampaign?.open === true && publicCampaign?.offerType === "year_end";
  const showStudentCampaignActivationBanner =
    user?.role === "student" &&
    entitlements != null &&
    entitlements?.tutoringIncluded !== true &&
    myCampaign?.open === true &&
    myCampaign?.activated !== true;
  const showAdminCampaignPreviewBanner =
    user?.role === "admin" && publicCampaign?.open === true;
  const campaignBannerData = showAdminCampaignPreviewBanner ? publicCampaign : myCampaign;
  const campaignActivationBanner =
    showStudentCampaignActivationBanner || showAdminCampaignPreviewBanner ? (
    <div
      className="glass-container"
      style={{
        width: "100%",
        borderRadius: 14,
        padding: viewportWidth <= 620 ? "14px" : "14px 16px",
        border: "1px solid rgba(255,153,0,0.78)",
        background: "linear-gradient(110deg, rgba(255,153,0,0.16), rgba(255,153,0,0.05))",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        lineHeight: 1.45,
      }}
    >
      <div style={{ flex: "1 1 360px" }}>
        <div style={{ color: "var(--color-accent)", fontSize: "0.76rem", fontWeight: 900 }}>
          {showAdminCampaignPreviewBanner ? "PODGLĄD ADMINISTRATORA" : "PROMOCJA STARTOWA"}
        </div>
        <strong>
          {campaignBannerData.offerType === "thirty_days"
            ? `Odbierz ${campaignBannerData.offerDays || 30} dni bezpłatnego dostępu`
            : `Odbierz bezpłatny dostęp do ${formatCampaignDate(campaignBannerData.accessValidTo)}`}
        </strong>
        <div style={{ marginTop: 2, fontSize: "0.88rem", opacity: 0.86 }}>
          {showAdminCampaignPreviewBanner ? (
            <>Tak ten stały baner widzi kwalifikujący się uczeń. Konto administratora nie aktywuje promocji.</>
          ) : (
            <>
              Aktywuj ofertę do {formatCampaignDate(campaignBannerData.offerEndsAt)}. Samo założenie konta
              nie uruchamia okresu dostępu.
            </>
          )}
        </div>
      </div>
      <Link
        to="/konto/subskrypcja"
        className="btn-primary"
        onClick={() =>
          trackMarketingEvent("promo_activation_cta_click", {
            placement: "logged_home",
            campaign_id: campaignBannerData.id || campaignBannerData.offerId || "",
          })
        }
        style={{ textDecoration: "none", borderRadius: 11, padding: "10px 15px", whiteSpace: "nowrap" }}
      >
        {showAdminCampaignPreviewBanner ? "Zobacz w Mojej subskrypcji" : "Aktywuj bezpłatny dostęp"}
      </Link>
    </div>
  ) : null;
  const campaignCurrentPrice = Number(myCampaign?.campaignCurrentPrice);
  const campaignRegularPrice = Number(myCampaign?.campaignRegularPrice);
  const showCampaignSaleBanner = Boolean(
    user?.role === "student" &&
    entitlements?.active === true &&
    entitlements?.tutoringIncluded !== true &&
    entitlements?.sku !== "p4" &&
    myCampaign?.campaignPurchaseAvailable === true &&
    myCampaign?.campaignPriceTier !== "regular" &&
    Number.isFinite(campaignCurrentPrice) &&
    Number.isFinite(campaignRegularPrice) &&
    campaignCurrentPrice > 0 &&
    campaignCurrentPrice < campaignRegularPrice &&
    !campaignSaleDismissed
  );
  const campaignSaleBanner = showCampaignSaleBanner ? (
    <div
      className="glass-container"
      style={{
        width: "100%",
        borderRadius: 14,
        padding: viewportWidth <= 620 ? "14px" : "14px 16px",
        border: "1px solid rgba(255,153,0,0.78)",
        background: "linear-gradient(110deg, rgba(255,153,0,0.2), rgba(255,153,0,0.06))",
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1 1 360px", lineHeight: 1.45 }}>
        <div style={{ color: "var(--color-accent)", fontSize: "0.76rem", fontWeight: 900 }}>
          OFERTA DLA CIEBIE
        </div>
        <strong>
          „Spokojnie do matury” za {campaignCurrentPrice.toFixed(0)} zł zamiast {campaignRegularPrice.toFixed(0)} zł
        </strong>
        <div style={{ marginTop: 2, fontSize: "0.88rem", opacity: 0.86 }}>
          {myCampaign.campaignCurrentPriceEndsAt
            ? `Cena obowiązuje do ${formatCampaignDate(myCampaign.campaignCurrentPriceEndsAt)}.`
            : "To aktualna cena dla uczestników kampanii."}
        </div>
      </div>
      <Link
        to="/konto/subskrypcja"
        className="btn-primary"
        onClick={() => trackMarketingEvent("campaign_sale_banner_click", { placement: "logged_home" })}
        style={{ textDecoration: "none", borderRadius: 11, padding: "10px 15px", whiteSpace: "nowrap" }}
      >
        Zobacz ofertę
      </Link>
      <button
        type="button"
        onClick={dismissCampaignSale}
        aria-label="Ukryj ofertę do jutra"
        title="Ukryj do jutra"
        style={{
          border: 0,
          background: "transparent",
          color: "var(--color-text)",
          opacity: 0.72,
          cursor: "pointer",
          fontSize: 23,
          lineHeight: 1,
          padding: 4,
        }}
      >
        ×
      </button>
    </div>
  ) : null;
  if (accessLevel === "guest") {
    const justifyStyle = {
      fontSize: "0.98rem",
      maxWidth: "780px",
      textAlign: "justify",
      textJustify: "inter-word",
      opacity: 0.9,
      lineHeight: 1.6,
      margin: "0 auto",
    };

    const bulletStyle = {
      ...justifyStyle,
      marginTop: "0.55rem",
      marginBottom: "0.95rem",
      paddingLeft: "1.2rem",
    };

    const sectionTitleStyle = {
      fontSize: "1rem",
      fontWeight: 750,
      margin: "0.85rem auto 0.35rem",
      maxWidth: "780px",
      textAlign: "left",
      opacity: 0.95,
    };

    const noteStyle = {
      ...justifyStyle,
      opacity: 0.85,
      marginTop: "0.15rem",
    };

    heroBlock = (
      <div
        style={{
          width: "100%",
          textAlign: "center",
          margin: "0 auto 1.6rem",
          padding: "0.2rem 0 0.2rem",
        }}
      >
        <div style={{ fontSize: "1.65rem", fontWeight: 800, lineHeight: 1.15, marginBottom: "0.55rem" }}>
          Sprawdź, czy zdasz maturę z matematyki
        </div>

        <div style={{ fontSize: "1.05rem", opacity: 0.92, margin: "0 auto 1.1rem", maxWidth: 760 }}>
          Rozwiąż <Accent>interaktywny arkusz maturalny</Accent> – sprawdzany natychmiast, z dokładnym
          wyjaśnieniem zadań i analizą.
        </div>

        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link
            to="/rejestracja"
            className="btn-primary"
            onClick={() =>
              trackMarketingEvent("landing_cta_click", {
                cta: "registration",
                destination: "/rejestracja",
              })
            }
            style={{ borderRadius: 12, padding: "10px 16px", textDecoration: "none" }}
          >
            Załóż bezpłatne konto
          </Link>

          <Link
            to="/demo/dzial/1"
            className="btn-secondary"
            style={{ borderRadius: 12, padding: "10px 16px", textDecoration: "none" }}
          >
            Zobacz lekcję pokazową
          </Link>

          <button
            type="button"
            className="btn-secondary"
            onClick={handleOpenDemoMatura}
            disabled={busyDemoMatura}
            style={{
              borderRadius: 12,
              padding: "10px 16px",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {busyDemoMatura ? "⏳ Uruchamiam demo matury…" : "Zadania maturalne – demo"}
          </button>
        </div>

        <div style={{ marginTop: "0.9rem", opacity: 0.9, fontSize: "0.92rem" }}>
          <div>✓ indywidualnie generowany arkusz maturalny</div>
          <div>✓ arkusz opracowany na podstawie arkuszy CKE</div>
          <div>✓ zadania zamknięte i otwarte, losowane warianty i dane</div>
          <div>✓ liczony czas pracy – możliwa pauza i kontynuacja rozpoczętego arkusza</div>
          <div>✓ automatyczne sprawdzanie i czytelne podsumowanie</div>
          <div>✓ zapisy i podsumowanie podejść w panelu maturzysty</div>
        </div>
      </div>
    );

    headerBlock = (
      <>
        <p
          style={{
            fontSize: "1.1rem",
            marginBottom: "0.9rem",
            textAlign: "center",
          }}
        >
          Czym jest Matematron?
        </p>

        <p style={{ ...justifyStyle, marginBottom: "0.6rem" }}>
          Matematron to <Accent>platforma</Accent> do nauki matematyki, która w jednym miejscu łączy teorię,
          ćwiczenia oraz sprawdzanie postępów – tak, aby szybciej przechodzić od zrozumienia materiału do
          pewności w rozwiązywaniu zadań.
        </p>

        <div style={sectionTitleStyle}>Dla kogo?</div>
        <ul style={bulletStyle}>
          <li>dla uczniów szkoły podstawowej i szkół średnich (liceum/technikum),</li>
          <li>do nauki na sprawdziany i kartkówki oraz do systematycznych powtórek,</li>
          <li>
            do przygotowania do <Accent>matury</Accent> – etapami, z analizą wyników.
          </li>
        </ul>

        <div style={sectionTitleStyle}>Na platformie znajdziesz:</div>
        <ul style={bulletStyle}>
          <li>zwięźle podaną teorię, ułożoną tematycznie,</li>
          <li>ćwiczenia z zadaniami o zróżnicowanym poziomie trudności, w tym przykładami maturalnymi,</li>
          <li>
            zadania generowane dynamicznie (różne warianty tego samego typu), dzięki czemu można ćwiczyć
            dany schemat wielokrotnie,
          </li>
          <li>
            <Accent>quizy</Accent> sprawdzające opanowanie tematu oraz odpowiedzi i wyjaśnienia krok po kroku do
            każdego ćwiczenia,
          </li>
          <li>
            panel ucznia, w którym zapisywane są podejścia do quizów i tematów – możesz wracać do swoich
            odpowiedzi, analizować błędy i obserwować postęp w czasie.
          </li>
        </ul>

        <div style={sectionTitleStyle}>Panel maturzysty:</div>
        <ul style={bulletStyle}>
          <li>
            rozwiązywanie pełnych arkuszy generowanych na podstawie zadań maturalnych i arkuszy{" "}
            <Accent>CKE</Accent>,
          </li>
          <li>sprawdzanie arkuszy w sposób analogiczny do quizów oraz czytelne podsumowanie wyników,</li>
          <li>analiza postępu i wyników w czasie,</li>
          <li>
            zapisy podejść – możliwość powrotu do rozwiązywanych arkuszy i quizów wraz z odpowiedziami oraz
            pełnymi wyjaśnieniami krok po kroku.
          </li>
        </ul>

        <p style={noteStyle}>
          Poniżej możesz bez zakładania konta zobaczyć przykład działania platformy. Aby odblokować całość,
          wystarczy utworzenie konta. Gdy oferta startowa jest aktywna, po potwierdzeniu adresu e-mail
          możesz świadomie odebrać bezpłatny dostęp w panelu.
          To dobry sposób, aby spokojnie sprawdzić platformę, przerobić wybrany temat lub rozwiązać arkusz i
          przeanalizować wynik.
        </p>
      </>
    );

    tilesBlock = (
      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "20px",
          marginTop: "2rem",
          alignItems: "stretch",
        }}
      >
        <LandingTile
          to="/demo/dzial/1"
          title="Lekcja pokazowa – Algebra: nierówności"
          description="Zobacz, jak wygląda nauka na platformie na przykładzie jednego tematu. W wersji demo możesz bez zakładania konta przejrzeć przykład jednego tematu."
        />

        <LandingTile
          to="/demo/matura/sciezka?mode=diagnosis#matura-exam-start"
          title="Zadania maturalne (demo)"
          description="Krótki zestaw zadań maturalnych (zamknięte i otwarte) z natychmiastowym sprawdzeniem i wyjaśnieniami. Bez zakładania konta."
          onClick={handleOpenDemoMatura}
          busy={busyDemoMatura}
          ctaLabel={busyDemoMatura ? "⏳ Uruchamiam demo matury…" : "Otwórz →"}
        />
      </div>
    );

    // Publiczny widok startowy skupiony na przygotowaniu do matury.
    headerBlock = null;
    searchBlock = null;
    tilesBlock = null;
    heroBlock = (
      <section
        style={{
          width: "100%",
          minHeight: viewportWidth <= 680 ? 350 : 410,
          padding: viewportWidth <= 680 ? "22px 18px" : "34px",
          border: isLightTheme
            ? "1px solid rgba(20,112,69,0.38)"
            : "1px solid rgba(255,167,38,0.7)",
          borderRadius: 8,
          display: "grid",
          gridTemplateColumns: viewportWidth <= 680 ? "1fr" : "minmax(0, 1fr) 240px",
          gap: viewportWidth <= 680 ? 18 : 34,
          alignItems: "center",
          overflow: "hidden",
          textAlign: "left",
          color: isLightTheme ? "#2b1a08" : "#fff",
          background: isLightTheme
            ? "linear-gradient(115deg, #fffaf2 0%, #fff1d8 58%, #f8dfb5 100%)"
            : "linear-gradient(115deg, #17143d 0%, #090726 62%, #050419 100%)",
          boxShadow: isLightTheme
            ? "0 18px 38px rgba(125,76,16,0.14)"
            : "0 18px 38px rgba(0,0,0,0.32)",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              marginBottom: 8,
              color: "#f39a0b",
              fontSize: "0.8rem",
              fontWeight: 900,
              textTransform: "uppercase",
            }}
          >
            Kurs maturalny z matematyki
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: viewportWidth <= 680 ? "1.65rem" : "2.25rem",
              lineHeight: 1.08,
              fontWeight: 900,
            }}
          >
            Matematron
          </h1>
          <p
            style={{
              maxWidth: 620,
              margin: "13px 0 18px",
              fontSize: viewportWidth <= 680 ? "0.94rem" : "1.05rem",
              lineHeight: 1.5,
              opacity: 0.86,
            }}
          >
            Kompleksowe przygotowanie do matury podstawowej: ćwiczenia, quizy,
            pełne arkusze i ścieżka, która porządkuje naukę od najważniejszych zagadnień.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {!yearEndCampaignOpen ? (
              <Link
                to="/rejestracja"
                onClick={() =>
                  trackMarketingEvent("landing_cta_click", {
                    cta: "registration",
                    destination: "/rejestracja",
                  })
                }
                className="btn-primary"
                style={{ textDecoration: "none", borderRadius: 12, padding: "11px 16px" }}
              >
                Załóż bezpłatne konto
              </Link>
            ) : null}
            <Link
              to="/demo/matura/sciezka"
              onClick={() =>
                trackMarketingEvent("landing_cta_click", {
                  cta: "matura_path_demo",
                  destination: "/demo/matura/sciezka",
                })
              }
              className="btn-secondary"
              style={{
                textDecoration: "none",
                borderRadius: 12,
                padding: "10px 16px",
                color: "inherit",
                borderColor: "currentColor",
              }}
            >
              Sprawdź ścieżkę demo
            </Link>
            <Link
              to="/demo/matura/sciezka?mode=diagnosis#matura-exam-start"
              onClick={() =>
                trackMarketingEvent("landing_cta_click", {
                  cta: "demo_exam",
                  destination: "/demo/matura/sciezka?mode=diagnosis#matura-exam-start",
                })
              }
              className="btn-secondary"
              style={{
                textDecoration: "none",
                borderRadius: 12,
                padding: "10px 16px",
                color: "inherit",
                borderColor: "currentColor",
              }}
            >
              Otwórz arkusz demo
            </Link>
          </div>
          {yearEndCampaignOpen ? (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,153,0,0.5)",
                background: "rgba(255,153,0,0.08)",
                fontSize: "0.9rem",
                lineHeight: 1.45,
              }}
            >
              <strong>Oferta startowa:</strong> załóż konto do{" "}
              <strong>{formatCampaignDate(publicCampaign.offerEndsAt)}</strong>.
              <div style={{ marginTop: 2 }}>
                Następnie aktywuj <strong>pełny, bezpłatny dostęp do kursu maturalnego do{" "}
                {formatCampaignDate(publicCampaign.accessValidTo)}</strong>.
              </div>
              <div style={{ marginTop: 8 }}>
                <Link
                  to="/rejestracja"
                  className="btn-primary"
                  onClick={() =>
                    trackMarketingEvent("landing_cta_click", {
                      cta: "campaign_registration",
                      destination: "/rejestracja",
                    })
                  }
                  style={{ display: "inline-block", textDecoration: "none", borderRadius: 10, padding: "8px 12px" }}
                >
                  Załóż konto i odbierz dostęp
                </Link>
              </div>
            </div>
          ) : null}
          <div style={{ marginTop: 10, fontSize: "0.82rem", lineHeight: 1.4, opacity: 0.72 }}>
            Ścieżkę i arkusz możesz sprawdzić bez rejestracji w 15-minutowym trybie demonstracyjnym.
            Aby kontynuować i zapisywać wyniki, załóż bezpłatne konto.
          </div>
        </div>

        <div
          aria-hidden="true"
          style={{
            display: viewportWidth <= 680 ? "none" : "grid",
            gap: 10,
            alignSelf: "stretch",
            alignContent: "center",
          }}
        >
          {["Ucz się po kolei", "Sprawdzaj się w quizach", "Rozwiązuj pełne arkusze"].map((label, index) => (
            <div
              key={label}
              style={{
                minHeight: 58,
                padding: "11px 13px",
                borderRadius: 8,
                border: "1px solid rgba(255,167,38,0.62)",
                background: isLightTheme ? "rgba(255,255,255,0.5)" : "rgba(9,7,38,0.78)",
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontWeight: 800,
                fontSize: "0.88rem",
              }}
            >
              <span style={{ color: "#f39a0b", fontSize: "1rem" }}>0{index + 1}</span>
              {label}
            </div>
          ))}
        </div>
      </section>
    );
  } else {
    headerBlock = null;

    const dashboardCols = viewportWidth <= 680 ? 1 : 2;
    const toolCols = viewportWidth <= 680 ? 1 : 2;
    const sectionShellStyle = {
      width: "100%",
      maxWidth: "980px",
      margin: "0 auto",
      display: "grid",
      gap: "10px",
    };
    const sectionHeaderStyle = {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    };
    const sectionTitleDashboardStyle = {
      fontSize: "0.95rem",
      fontWeight: 850,
      letterSpacing: 0,
      opacity: 0.95,
    };
    const sectionHintStyle = {
      fontSize: "0.82rem",
      opacity: 0.72,
      lineHeight: 1.35,
    };
    const chipStyle = (active = false) => ({
      border: active ? "1px solid var(--color-accent)" : "1px solid rgba(255,255,255,0.24)",
      background: active ? "rgba(255,167,38,0.16)" : "rgba(255,255,255,0.045)",
      color: "var(--color-text)",
      borderRadius: 999,
      padding: "7px 10px",
      fontFamily: "var(--font-main)",
      fontSize: "0.82rem",
      fontWeight: 750,
      lineHeight: 1.1,
    });
    const profileOptionStyle = (active = false, disabled = false) => ({
      border: active ? "1px solid var(--color-accent)" : "1px solid rgba(255,255,255,0.24)",
      background: active ? "rgba(255,167,38,0.17)" : "rgba(255,255,255,0.045)",
      color: disabled ? "rgba(255,255,255,0.45)" : "var(--color-text)",
      borderRadius: 12,
      padding: "9px 11px",
      cursor: disabled ? "not-allowed" : "pointer",
      fontFamily: "var(--font-main)",
      fontSize: "0.84rem",
      fontWeight: 750,
      lineHeight: 1.15,
      textAlign: "left",
    });
    const toolCardStyle = {
      textDecoration: "none",
      color: "inherit",
      borderRadius: 16,
      padding: "16px",
      border: "1px solid #ffa726",
      minHeight: 165,
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gap: 14,
      alignItems: "center",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
    };
    const toolIconWrapStyle = {
      width: viewportWidth <= 680 ? 64 : 82,
      height: viewportWidth <= 680 ? 64 : 82,
      borderRadius: 16,
      border: "1px solid rgba(255,167,38,0.55)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "var(--color-accent)",
      flexShrink: 0,
    };
    const toolLargeIconStyle = {
      width: viewportWidth <= 680 ? 42 : 54,
      height: viewportWidth <= 680 ? 42 : 54,
      display: "block",
    };
    const showMaturaPanel = !studentLearningProfile.stage || studentLearningProfile.stage === "secondary";
    const showEighthGradePanel = !studentLearningProfile.stage || studentLearningProfile.stage === "primary";

    tilesBlock = (
      <div
        style={{
          width: "100%",
          display: "grid",
          gap: "18px",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "980px",
            minHeight: viewportWidth <= 680 ? 220 : 230,
            margin: "0 auto",
            padding: viewportWidth <= 680 ? "18px" : "22px 26px",
            borderRadius: 8,
            border: isLightTheme
              ? "1px solid rgba(255,153,0,0.5)"
              : "1px solid rgba(255,167,38,0.7)",
            color: isLightTheme ? "#17132b" : "#fff",
            display: "grid",
            gridTemplateColumns: viewportWidth <= 680 ? "1fr" : "minmax(0, 1fr) 330px",
            gap: viewportWidth <= 680 ? 18 : 28,
            alignItems: "center",
            overflow: "hidden",
            background: isLightTheme
              ? "linear-gradient(115deg, #fffaf0 0%, #fff 58%, #f3efff 100%)"
              : "linear-gradient(115deg, #15105f 0%, #07072f 58%, #151025 100%)",
            boxShadow: isLightTheme
              ? "0 16px 34px rgba(64,44,120,0.15)"
              : "0 16px 34px rgba(0,0,0,0.3)",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                color: "#ff9900",
                fontSize: "0.78rem",
                fontWeight: 850,
                textTransform: "uppercase",
                marginBottom: 7,
              }}
            >
              Twoja ścieżka maturalna
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: viewportWidth <= 680 ? "1.35rem" : "1.75rem",
                lineHeight: 1.08,
                fontWeight: 900,
              }}
            >
              Kontynuuj przygotowanie do matury
            </h2>
            <p
              style={{
                margin: "9px 0 14px",
                maxWidth: 590,
                fontSize: viewportWidth <= 680 ? "0.86rem" : "0.95rem",
                lineHeight: 1.45,
                color: isLightTheme ? "rgba(23,19,43,0.78)" : "rgba(255,255,255,0.82)",
              }}
            >
              Ucz się po kolei, ćwicz zadania maturalne i sprawdzaj postęp na pełnych arkuszach.
            </p>
            <div style={{ fontSize: "0.84rem", lineHeight: 1.45, opacity: 0.78 }}>
              Ścieżka dobiera zakres do Twojego celu, pokazuje postęp i prowadzi od przygotowania
              przez ćwiczenia aż do quizów oraz sprawdzianów działowych.
            </div>
          </div>
          <MaturaLandingSummary
            user={user}
            authorizedFetch={authorizedFetch}
            isLightTheme={isLightTheme}
            viewportWidth={viewportWidth}
          />
        </div>

        {canUseTutoringTools && (
          <section
            id="admin-extended-path-preview"
            aria-labelledby="admin-extended-path-title"
            style={{
              width: "100%", maxWidth: "980px", boxSizing: "border-box",
              margin: "0 auto", padding: viewportWidth <= 680 ? "18px" : "22px 26px",
              borderRadius: 8,
              border: isLightTheme ? "1px solid rgba(255,153,0,0.5)" : "1px solid rgba(255,167,38,0.7)",
              color: isLightTheme ? "#17132b" : "#fff",
              display: "grid", gridTemplateColumns: viewportWidth <= 680 ? "1fr" : "minmax(0, 1fr) 330px",
              gap: viewportWidth <= 680 ? 18 : 28, alignItems: "center",
              background: isLightTheme
                ? "linear-gradient(115deg, #fffaf0 0%, #fff 58%, #f3efff 100%)"
                : "linear-gradient(115deg, #15105f 0%, #07072f 58%, #151025 100%)",
              boxShadow: isLightTheme ? "0 16px 34px rgba(64,44,120,0.15)" : "0 16px 34px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#ff9900", fontSize: "0.875rem", fontWeight: 850, textTransform: "uppercase", marginBottom: 7 }}>
                Ścieżka matury rozszerzonej
              </div>
              <h2 id="admin-extended-path-title" style={{ margin: 0, fontSize: viewportWidth <= 680 ? "1.35rem" : "1.75rem", lineHeight: 1.15, fontWeight: 900 }}>
                Przygotowanie do matury rozszerzonej
              </h2>
              <p style={{ margin: "12px 0 0", fontSize: "1rem", lineHeight: 1.5, opacity: 0.82 }}>
                Przeglądaj zagadnienia, otwieraj zadania z rozwiązaniami i ćwicz na dostępnych arkuszach CKE.
              </p>
            </div>
            <div style={{ minWidth: 0, display: "grid", gap: 14 }}>
              <div style={{ padding: "14px 16px", borderRadius: 10, border: "1px solid var(--color-border-subtle)", background: "var(--color-elevated-bg)" }}>
                <strong style={{ fontSize: "1rem" }}>Dostęp w ramach korepetycji · wersja testowa</strong>
                <p style={{ margin: "8px 0 0", fontSize: "0.95rem", lineHeight: 1.5, opacity: 0.8 }}>
                  10 działów i 20 zagadnień. Ćwiczenia, quizy i oryginalne zadania z 10 arkuszy CKE. Na razie bez przygotowań tematycznych.
                </p>
              </div>
              <Link to="/matura/rozszerzenie" className="btn-primary"
                style={{ display: "block", textAlign: "center", whiteSpace: "normal", padding: "16px 20px", fontSize: "1rem", lineHeight: 1.4, textDecoration: "none" }}>
                Przejdź do ścieżki rozszerzonej →
              </Link>
            </div>
          </section>
        )}

        {isAdmin && (
          <section
            id="admin-e8-path-preview"
            aria-labelledby="admin-e8-path-title"
            style={{
              width: "100%", maxWidth: "980px", boxSizing: "border-box",
              margin: "0 auto", padding: viewportWidth <= 680 ? "18px" : "22px 26px",
              borderRadius: 8,
              border: isLightTheme ? "1px solid rgba(255,153,0,0.5)" : "1px solid rgba(255,167,38,0.7)",
              color: isLightTheme ? "#17132b" : "#fff",
              display: "grid", gridTemplateColumns: viewportWidth <= 680 ? "1fr" : "minmax(0, 1fr) 330px",
              gap: viewportWidth <= 680 ? 18 : 28, alignItems: "center",
              background: isLightTheme
                ? "linear-gradient(115deg, #fffaf0 0%, #fff 58%, #f3efff 100%)"
                : "linear-gradient(115deg, #15105f 0%, #07072f 58%, #151025 100%)",
              boxShadow: isLightTheme ? "0 16px 34px rgba(64,44,120,0.15)" : "0 16px 34px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#ff9900", fontSize: "0.875rem", fontWeight: 850, textTransform: "uppercase", marginBottom: 7 }}>
                Ścieżka egzaminu ósmoklasisty
              </div>
              <h2 id="admin-e8-path-title" style={{ margin: 0, fontSize: viewportWidth <= 680 ? "1.35rem" : "1.75rem", lineHeight: 1.15, fontWeight: 900 }}>
                Przygotowanie do egzaminu ósmoklasisty
              </h2>
              <p style={{ margin: "12px 0 0", fontSize: "1rem", lineHeight: 1.5, opacity: 0.82 }}>
                Przeglądaj zagadnienia, otwieraj zadania z rozwiązaniami i ćwicz na dostępnych arkuszach CKE.
              </p>
            </div>
            <div style={{ minWidth: 0, display: "grid", gap: 14 }}>
              <div style={{ padding: "14px 16px", borderRadius: 10, border: "1px solid var(--color-border-subtle)", background: "var(--color-elevated-bg)" }}>
                <strong style={{ fontSize: "1rem" }}>Podgląd administratora · wersja testowa</strong>
                <p style={{ margin: "8px 0 0", fontSize: "0.95rem", lineHeight: 1.5, opacity: 0.8 }}>
                  Dostępne są zagadnienia, przygotowania, quizy, sprawdziany i zapis wyników. Prognoza i kalendarz są w przygotowaniu.
                </p>
              </div>
              <Link to="/egzamin-osmoklasisty/sciezka" className="btn-primary"
                style={{ display: "block", textAlign: "center", whiteSpace: "normal", padding: "16px 20px", fontSize: "1rem", lineHeight: 1.4, textDecoration: "none" }}>
                Przejdź do ścieżki ósmoklasisty →
              </Link>
            </div>
          </section>
        )}

        {isAdmin && canSeeLearningPathExperiments && !learningProfileDismissed && (
          <div
            className="glass-container"
            style={{
              width: "100%",
              maxWidth: "980px",
              margin: "0 auto",
              borderRadius: 16,
              padding: "12px 14px",
              border: "1px solid rgba(255,167,38,0.42)",
              display: "grid",
              gridTemplateColumns: viewportWidth <= 720 ? "1fr" : "minmax(0, 1fr) auto",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: "0.98rem", fontWeight: 850, marginBottom: 4 }}>
                Uzupełnij ścieżkę nauki
              </div>
              <div style={{ fontSize: "0.86rem", lineHeight: 1.4, opacity: 0.86 }}>
                Klasa i książka pomogą później podpowiadać właściwe lekcje, działy i panele egzaminacyjne.
                Szkołę zostawiamy na kolejny etap.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                <span style={chipStyle(!!studentLearningProfile.grade)}>
                  Klasa {studentLearningProfile.grade || ""}
                </span>
                <span style={chipStyle(!!studentLearningStage)}>
                  {studentLearningStage?.label || "Etap"}
                </span>
                {studentLearningStage?.id === "secondary" && (
                  <span style={chipStyle(!!selectedStudentLevel)}>
                    {selectedStudentLevel?.label || "Poziom"}
                  </span>
                )}
                <span style={chipStyle(!!selectedStudentPublisher)}>
                  {selectedStudentPublisher?.label || "Książka"}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: viewportWidth <= 720 ? "flex-start" : "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setLearningProfileExpanded((value) => !value)}
                style={{ borderRadius: 12, padding: "9px 12px" }}
              >
                {learningProfileExpanded ? "Zwiń" : "Uzupełnij teraz"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  dismissLearningProfilePrompt();
                  setShowLearningProfileLaterInfo(true);
                }}
                style={{ borderRadius: 12, padding: "9px 12px" }}
              >
                Uzupełnij później
              </button>
              <button
                type="button"
                onClick={dismissLearningProfilePrompt}
                title="Zamknij"
                aria-label="Zamknij panel ścieżki nauki"
                style={{
                  display: "none",
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.28)",
                  background: "transparent",
                  color: "var(--color-text)",
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {isAdmin && canSeeLearningPathExperiments && !learningProfileDismissed && learningProfileExpanded && (
          <div
            className="glass-container"
            style={{
              width: "100%",
              maxWidth: "980px",
              margin: "-6px auto 0",
              borderRadius: 16,
              padding: "14px",
              border: "1px solid rgba(255,167,38,0.32)",
              display: "grid",
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: "0.78rem", opacity: 0.72, marginBottom: 6 }}>Etap</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
                {learningPathStages.map((stage) => (
                  <button
                    key={stage.id}
                    type="button"
                    style={profileOptionStyle(studentLearningProfile.stage === stage.id)}
                    onClick={() => updateStudentLearningProfile({ stage: stage.id })}
                  >
                    {stage.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: "0.78rem", opacity: 0.72, marginBottom: 6 }}>Klasa</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {(studentLearningStage?.grades || []).map((grade) => (
                  <button
                    key={grade}
                    type="button"
                    style={profileOptionStyle(studentLearningProfile.grade === grade)}
                    onClick={() => updateStudentLearningProfile({ grade })}
                  >
                    {grade}
                  </button>
                ))}
                {!studentLearningStage && (
                  <button type="button" disabled style={profileOptionStyle(false, true)}>
                    Najpierw wybierz etap
                  </button>
                )}
              </div>
            </div>

            {studentLearningStage?.id === "secondary" && (
              <div>
                <div style={{ fontSize: "0.78rem", opacity: 0.72, marginBottom: 6 }}>Poziom</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {secondaryLearningLevels.map((level) => (
                    <button
                      key={level.id}
                      type="button"
                      style={profileOptionStyle(studentLearningProfile.level === level.id)}
                      onClick={() => updateStudentLearningProfile({ level: level.id })}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div style={{ fontSize: "0.78rem", opacity: 0.72, marginBottom: 6 }}>Książka / wydawnictwo</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 8 }}>
                {studentPublisherOptions.map((publisher) => (
                  <button
                    key={publisher.id}
                    type="button"
                    style={profileOptionStyle(studentLearningProfile.publisher === publisher.id)}
                    onClick={() => updateStudentLearningProfile({ publisher: publisher.id })}
                  >
                    {publisher.label}
                  </button>
                ))}
                {!studentLearningStage && (
                  <button type="button" disabled style={profileOptionStyle(false, true)}>
                    Najpierw wybierz etap
                  </button>
                )}
                {studentLearningStage?.id === "secondary" && !studentLearningProfile.level && (
                  <button type="button" disabled style={profileOptionStyle(false, true)}>
                    Najpierw wybierz poziom
                  </button>
                )}
              </div>
            </div>

            <div style={{ fontSize: "0.82rem", opacity: 0.78, lineHeight: 1.4 }}>
              Bez wyboru książki pokażemy ogólną ścieżkę działów. Mapowanie spisów treści z książek
              podepniemy w kolejnym kroku.
            </div>
          </div>
        )}

        {isAdmin && canSeeLearningPathExperiments && showLearningProfileLaterInfo && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1200,
              background: "rgba(0,0,0,0.62)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 18,
            }}
            onClick={() => setShowLearningProfileLaterInfo(false)}
          >
            <div
              className="glass-container"
              style={{
                width: "min(520px, 100%)",
                borderRadius: 16,
                padding: 18,
                border: "1px solid rgba(255,167,38,0.45)",
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={{ fontSize: "1.04rem", fontWeight: 850, marginBottom: 8 }}>
                Wrócimy do tego później
              </div>
              <div style={{ fontSize: "0.9rem", lineHeight: 1.45, opacity: 0.86 }}>
                Na razie panel znika tylko do odświeżenia strony. Docelowo ustawienia klasy i książki
                będą dostępne w Panelu użytkownika.
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setShowLearningProfileLaterInfo(false)}
                  style={{ borderRadius: 12, padding: "9px 14px" }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {canUseTutoringTools ? (
          <div style={sectionShellStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <div style={sectionTitleDashboardStyle}>Narzędzia</div>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${toolCols}, minmax(0, 1fr))`,
                gap: "12px",
                alignItems: "stretch",
              }}
            >
              <Link to="/tablica" className="glass-container" style={toolCardStyle}>
                <div>
                  <h2 style={{ fontSize: "1.08rem", fontWeight: 800, marginBottom: 8 }}>Interaktywna tablica</h2>
                  <p style={{ fontSize: "0.9rem", lineHeight: 1.45, opacity: 0.9, marginBottom: 14 }}>
                    Tablica do rozwiązywania zadań, pracy na żywo i wspólnego omawiania kroków.
                  </p>
                  <span style={{ fontSize: "0.9rem", fontWeight: 750, opacity: 0.95 }}>
                    Otwórz →
                  </span>
                </div>
                <div style={toolIconWrapStyle}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={toolLargeIconStyle}>
                    <rect x="3" y="4" width="18" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M9 20h6M12 16v4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M7 9h10M7 12h6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </div>
              </Link>

              <Link to="/wykres" className="glass-container" style={toolCardStyle}>
                <div>
                  <h2 style={{ fontSize: "1.08rem", fontWeight: 800, marginBottom: 8 }}>Wykres funkcji</h2>
                  <p style={{ fontSize: "0.9rem", lineHeight: 1.45, opacity: 0.9, marginBottom: 14 }}>
                    Pełny widok do rysowania i sprawdzania wykresów, punktów oraz zależności.
                  </p>
                  <span style={{ fontSize: "0.9rem", fontWeight: 750, opacity: 0.95 }}>
                    Otwórz →
                  </span>
                </div>
                <div style={toolIconWrapStyle}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={toolLargeIconStyle}>
                    <path d="M4 19h17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M5 4v16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M6.5 16c2.1-6.4 4.5-9.4 7-9 2 .3 2.6 2.9 5.5 2.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="13.5" cy="7" r="1.3" fill="currentColor" />
                  </svg>
                </div>
              </Link>
            </div>
          </div>
        ) : null}

        <div style={sectionShellStyle}>
          <div style={sectionHeaderStyle}>
            <div>
              <div style={sectionTitleDashboardStyle}>Szybki start</div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${dashboardCols}, minmax(0, 1fr))`,
              gap: "12px",
              alignItems: "stretch",
            }}
          >
        <div style={{ width: "100%" }}>
          <LandingTile
            to="/home"
            icon={<SectionsMonoIcon />}
            title="Wybór działu"
            description="Luźne wejście w platformę: wybierz dział, temat i ćwicz dokładnie to, czego teraz potrzebujesz."
            minHeight={175}
            compact
          />
        </div>

        {isAdmin && canSeeLearningPathExperiments && (
        <div style={{ width: "100%" }}>
          <LandingTile
            to="/home"
            icon={<SectionsMonoIcon />}
            title="Tematy z książki"
            description={
              selectedStudentPublisher
                ? `Miejsce na spis treści: ${selectedStudentPublisher.label}. Podpięcie lekcji do książek dodamy w kolejnym kroku.`
                : "Po wyborze klasy i książki pokażemy tutaj działy według spisu treści."
            }
            disabled
            disabledNote={selectedStudentPublisher ? "Spis w przygotowaniu" : "Najpierw wybierz książkę"}
            minHeight={175}
            compact
          />
        </div>
        )}

        <div style={{ width: "100%" }}>
          <div
            className="glass-container"
            style={{
              borderRadius: 16,
              padding: "14px",
              border: "1px solid rgba(255,167,38,0.42)",
              minHeight: 175,
              display: "grid",
              gap: 10,
            }}
          >
            <div>
              <h2 style={{ fontSize: "1rem", fontWeight: 750, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <SearchMonoIcon />
                Szukaj lekcji
              </h2>
              <div
                className="glass-container"
                style={{
                  padding: "0.5rem 0.75rem",
                  borderRadius: 999,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ opacity: 0.75, display: "inline-flex" }}>
                  <SearchMonoIcon />
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder='np. "NWD", "logarytmy"'
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: "0.9rem",
                    color: "var(--color-text)",
                  }}
                />
              </div>
            </div>

            {trimmedSearch.length >= 3 ? (
              searchResults.length > 0 ? (
                <div style={{ display: "grid", gap: 6 }}>
                  {searchResults.slice(0, 3).map((item, idx) => {
                    const isLockedForStudent =
                      !isAdmin && lockedSubtopicsForStudents.has(String(item.id || ""));
                    const destination = item.isQuiz
                      ? `/quiz/${item.sectionIndex}/${item.subtopicIndex}`
                      : `/lekcja/${item.sectionIndex}/${item.subtopicIndex}`;

                    return (
                      <button
                        key={`${item.id}-${idx}`}
                        type="button"
                        disabled={isLockedForStudent}
                        onClick={() => {
                          if (!isLockedForStudent) navigate(destination);
                        }}
                        style={{
                          textAlign: "left",
                          border: "1px solid rgba(255,167,38,0.32)",
                          borderRadius: 10,
                          padding: "0.5rem 0.65rem",
                          background: "transparent",
                          color: "var(--color-text)",
                          cursor: isLockedForStudent ? "not-allowed" : "pointer",
                          opacity: isLockedForStudent ? 0.6 : 1,
                        }}
                      >
                        <div style={{ fontSize: "0.86rem", fontWeight: 800 }}>{item.subtopicName}</div>
                        <div style={{ marginTop: 3, fontSize: "0.76rem", opacity: 0.76 }}>
                          {item.sectionTitle} · {item.topicName}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: "0.86rem", opacity: 0.78 }}>
                  Brak wyników dla „{searchQuery.trim()}”.
                </div>
              )
            ) : (
              <div style={{ fontSize: "0.86rem", opacity: 0.78 }}>
                Wpisz co najmniej 3 znaki albo otwórz pełną wyszukiwarkę.
              </div>
            )}

            <Link to="/szukaj" style={{ color: "inherit", fontSize: "0.88rem", fontWeight: 750, textDecoration: "none" }}>
              Pełna wyszukiwarka →
            </Link>
          </div>
        </div>
          </div>
        </div>

        {isAdmin ? <div style={sectionShellStyle}>
          <div style={sectionHeaderStyle}>
            <div>
              <div style={sectionTitleDashboardStyle}>Egzaminy i cele</div>
              <div style={sectionHintStyle}>Panele egzaminacyjne docelowo pokażą tylko to, co pasuje do profilu ucznia.</div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${toolCols}, minmax(0, 1fr))`,
              gap: "12px",
              alignItems: "stretch",
            }}
          >

        {showMaturaPanel && (
        <div style={{ width: "100%" }}>
          <LandingTile
            to="/panel-maturzysty"
            icon={<ExamMonoIcon />}
            title="Panel maturzysty"
            description="Podejścia do arkuszy, wyniki, wykres postępu oraz szybki start kolejnego arkusza."
            minHeight={165}
            compact
          />
        </div>
        )}

        {showEighthGradePanel && (
        <div style={{ width: "100%" }}>
          <LandingTile
            to="/panel-osmoklasisty"
            icon={<ExamMonoIcon />}
            title="Panel ósmoklasisty"
            description="Format egzaminu, zadania zamknięte i otwarte oraz miejsce na pełne arkusze i analizę wyników."
            minHeight={165}
            compact
          />
        </div>
        )}
          </div>
        </div> : null}

      </div>
    );

    searchBlock = (
      <div
        className="glass-container"
        style={{
          width: "100%",
          maxWidth: "760px",
          margin: "0 auto",
          borderRadius: 14,
          padding: "12px",
          border: "1px solid rgba(255,167,38,0.35)",
        }}
      >
        <div style={{ fontSize: "0.93rem", fontWeight: 700, marginBottom: 8 }}>
          Wyszukaj lekcję <span style={{ fontWeight: 500, opacity: 0.85 }}>(wpisz co najmniej 3 znaki)</span>
        </div>

        <div
          className="glass-container"
          style={{
            padding: "0.52rem 0.82rem",
            borderRadius: 999,
            marginBottom: "0.5rem",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ opacity: 0.8, display: "inline-flex" }}>
            <SearchMonoIcon />
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Min. 3 znaki (np. "NWD", "logarytmy")'
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: "0.95rem",
              color: "var(--color-text)",
            }}
          />
        </div>

        {trimmedSearch.length < 3 ? null : searchResults.length === 0 ? (
          <div style={{ fontSize: "0.95rem", opacity: 0.85 }}>
            Brak wyników dla frazy <strong>„{searchQuery.trim()}”</strong>.
          </div>
        ) : (
          <div style={{ marginTop: "0.4rem", display: "grid", gap: 8 }}>
            {searchResults.slice(0, 12).map((item, idx) => {
              const isLockedForStudent =
                !isAdmin && lockedSubtopicsForStudents.has(String(item.id || ""));
              const destination = item.isQuiz
                ? `/quiz/${item.sectionIndex}/${item.subtopicIndex}`
                : `/lekcja/${item.sectionIndex}/${item.subtopicIndex}`;

              return (
                <button
                  key={`${item.id}-${idx}`}
                  type="button"
                  disabled={isLockedForStudent}
                  onClick={() => {
                    if (!isLockedForStudent) navigate(destination);
                  }}
                  className="glass-container"
                  style={{
                    textAlign: "left",
                    border: "1px solid rgba(255,167,38,0.45)",
                    borderRadius: 12,
                    padding: "0.7rem 0.85rem",
                    background: "transparent",
                    color: "var(--color-text)",
                    cursor: isLockedForStudent ? "not-allowed" : "pointer",
                    opacity: isLockedForStudent ? 0.6 : 1,
                  }}
                  title={isLockedForStudent ? "Ten podtemat jest chwilowo zablokowany dla ucznia." : undefined}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <strong style={{ fontSize: "0.96rem", color: "var(--color-text)" }}>
                      {item.subtopicName}
                    </strong>
                    {item.isQuiz ? (
                      <span
                        style={{
                          fontSize: "0.72rem",
                          padding: "2px 8px",
                          borderRadius: 999,
                          border: `1px solid ${ACCENT}`,
                          color: ACCENT,
                        }}
                      >
                        QUIZ
                      </span>
                    ) : null}
                    {isLockedForStudent ? (
                      <span
                        style={{
                          fontSize: "0.72rem",
                          padding: "2px 8px",
                          borderRadius: 999,
                          border: "1px solid rgba(220,80,80,0.9)",
                          color: "#dc5050",
                        }}
                      >
                        ZABLOKOWANE
                      </span>
                    ) : null}
                  </div>

                  <div style={{ marginTop: 5, fontSize: "0.88rem", opacity: 0.9, color: "var(--color-text)" }}>
                    {item.sectionTitle} • {item.topicName}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        boxSizing: "border-box",
        maxWidth: `${RIBBON_MAX_WIDTH}px`,
        width: "100%",
        margin: "0 auto",
        fontFamily: "var(--font-main)",
        color: "var(--color-text)",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {/* BANER INFO (guest: kod rabatowy, logged: early access + zgłaszanie) */}
      {topBanner}
      {myProgressBanner}
      {campaignSaleBanner}
      {campaignActivationBanner}

      {/* BANER: darmowy trial (student) + placeholder podglądu (admin) */}
      {showTrialBanner ? (
        <div
          className="glass-container"
          style={{
            width: "100%",
            borderRadius: 14,
            padding: "12px 14px",
            border: "1px solid rgba(0,180,255,0.35)",
            background: "rgba(0,180,255,0.06)",
            textAlign: "center",
            fontSize: "0.95rem",
            lineHeight: 1.35,
          }}
        >
          ⏳ Korzystasz z darmowego dostępu trial. Dostęp wygasa za{" "}
          <strong>{formatHms(trialLeftMs)}</strong> (do{" "}
          <strong>{formatDateTime(entitlements?.trialValidTo)}</strong>).
        </div>
      ) : null}
      {showAdminTrialPlaceholder ? (
        <div
          className="glass-container"
          style={{
            width: "100%",
            borderRadius: 14,
            padding: "12px 14px",
            border: "1px dashed rgba(0,180,255,0.5)",
            background: "rgba(0,180,255,0.05)",
            textAlign: "center",
            fontSize: "0.95rem",
            lineHeight: 1.35,
          }}
        >
          🧪 <strong>Podgląd admina:</strong> tutaj wyświetla się komunikat triala użytkownika
          (np. „Korzystasz z darmowego dostępu trial…”).
        </div>
      ) : null}

      {accessLevel === "guest" ? (
        <DemoMaturaNotice
          compact
          overviewMode
          onlyIfStarted
          showExpiredDialog={false}
        />
      ) : null}

      {/* PANEL 1: HERO (tylko gość) */}
      {heroBlock && (
        <div
          className="glass-container"
          style={{
            width: "100%",
            padding: "2.2rem 1.8rem 2.1rem",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div style={{ width: "100%" }}>{heroBlock}</div>
        </div>
      )}

      {/* PANEL 0: KOREPETYCJE (od razu pod headerem) */}
      {showBelowFold && accessLevel === "guest" && tilesBlock && <TutoringLeadPanel />}
      {accessLevel !== "guest" ? (
        <StudentTutoringShortcut
          user={user}
          authorizedFetch={authorizedFetch}
          reloadAccess={reloadAccess}
        />
      ) : null}

      {/* PANEL 2: OPIS + DEMO / albo NAWIGACJA (zalogowany) */}
      {showBelowFold && (headerBlock || tilesBlock || (accessLevel === "guest" && searchBlock)) ? (
        <div
          className="glass-container"
          style={{
            width: "100%",
            padding: "2.2rem 1.8rem 2.6rem",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {headerBlock ? (
            <div style={{ width: "100%", marginBottom: "1.4rem" }}>{headerBlock}</div>
          ) : null}
          {canSeeLearningPathExperiments && isAdmin ? <AdminLearningPathPanel /> : null}
          {accessLevel === "guest" ? searchBlock : null}
          {tilesBlock}
        </div>
      ) : null}

    </div>
  );
}
